import { Route } from '@angular/router';
import { environment } from 'environments/environment';

import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { Permission, SETTINGS_PERMISSIONS } from 'app/core/auth/permissions.enum';

import { BotSetupComponent } from '@modules/settings/bot-setup/bot-setup.component';

import { DemoComponent } from './demo/demo.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  {
    path: 'demo',
    component: DemoComponent,
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
  },
  { path: 'chat', loadChildren: () => import('app/modules/chat/chat.routes') },
  { path: 'demo-chat', loadChildren: () => import('app/modules/demo-chat/demo-chat.routes') },

  // Redirect signed-in user to the '/example'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: '/dashboard' },

  // Auth routes for guests
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes'),
      },
      {
        path: 'forgot-password',
        loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
      },
      { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
      { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
      { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') },
    ],
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
      { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') },
      {
        path: 'bot-setup',
        component: BotSetupComponent,
      },
    ],
  },

  // Landing routes
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [{ path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') }],
  },

  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('app/modules/dashboard/dashboard.routes'),
        data: {
          preload: true,
        },
      },
      {
        path: 'bots',
        loadChildren: () => import('app/modules/bots/bots-crud.routes'),
        data: {
          permissions: [Permission.BOT_ADMIN, Permission.BOT_ADMIN_ANY, Permission.BOT_READ, Permission.BOT_READ_ANY],
          preload: true,
        },
      },

      {
        path: 'settings',
        loadChildren: () => import('app/modules/settings/settings.routes'),
        data: {
          roles: ['AIVA_admin'],
          permissions: SETTINGS_PERMISSIONS,
        },
      },
      {
        path: 'execution-logs',
        loadChildren: () => import('app/modules/execution-logs/execution-logs.routes'),
      },
      {
        path: 'users',
        loadChildren: () => import('app/modules/users/users.routes'),
        data: {
          permissions: [Permission.USER_ADMIN, Permission.USER_ADMIN_ANY, Permission.USER_READ, Permission.USER_READ_ANY],
        },
      },
      {
        path: 'sub-accounts',
        loadChildren: () => import('app/modules/sub-accounts/sub-accounts.routes'),
        data: {
          permissions: [
            Permission.SUBACCOUNT_ADMIN,
            Permission.SUBACCOUNT_ADMIN_ANY,
            Permission.SUBACCOUNT_READ,
            Permission.SUBACCOUNT_READ_ANY,
          ],
        },
      },
      {
        path: 'accounts',
        loadChildren: () => import('app/modules/accounts/accounts.routes'),
        data: {
          permissions: [
            Permission.ACCOUNT_ADMIN,
            Permission.ACCOUNT_ADMIN_ANY,
            Permission.ACCOUNT_READ,
            Permission.ACCOUNT_READ_ANY,
          ],
        },
      },
      { path: 'permissions', loadChildren: () => import('app/core/permissions/permissions.routes') },
      {
        path: 'interaction-tracking',
        loadChildren: () => import('app/modules/interaction-tracking/interaction-tracking.routes'),
      },
      {
        path: 'performance-analytics',
        loadChildren: () => import('app/modules/performance-analytics/performance-analytics.routes'),
      },
      {
        path: 'voice-bakeoff',
        // Hidden from the nav AND unmatchable unless voiceBakeoff is on, so the
        // page can't be opened by URL in RC/prod.
        canMatch: [() => environment.voiceBakeoff ?? false],
        loadChildren: () => import('app/modules/voice-bakeoff/voice-bakeoff.routes'),
        data: {
          // Same gate as /settings, where this page now lives in the sidebar:
          // AuthGuard is OR logic, so AIVA_admin OR any settings permission
          // grants access. Previously this listed the role alone, which locked
          // out the permission-holders who can already reach Partners and the
          // Onboarding Wizard.
          roles: ['AIVA_admin'],
          permissions: SETTINGS_PERMISSIONS,
        },
      },
      {
        path: 'change-password',
        loadChildren: () => import('app/modules/auth/change-password/change-password.routes'),
      },
    ],
  },
];
